import { createContext, useContext } from 'react';
import type { ChannelScope, DestinationNode, ShippingChannel } from '@mypoz/shared';

/** Where one lazily-fetched slice of the catalog (one country's provinces, one province's cities)
 * has got to. `idle` means nobody has asked for it yet — the same vocabulary `OrderDetailStatus`
 * uses in `orders/context.ts`, for the same reason: a cascade select needs to tell "never asked"
 * apart from "asked and still waiting". */
export type CatalogFetchStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface OrderCatalogState {
  /**
   * Every channel `channels` returns — a name dictionary only, for showing which channel an order
   * already has. What a customer may *choose* comes from `channelsFor` (MYP-87).
   */
  readonly channels: readonly ShippingChannel[];
  /** The channels offered for one warehouse and goods type (`neworder_getChannel`). */
  channelsFor: (
    scope: Pick<ChannelScope, 'warehouseId' | 'goodsType'>,
  ) => readonly ShippingChannel[];
  channelsStatus: (scope: Pick<ChannelScope, 'warehouseId' | 'goodsType'>) => CatalogFetchStatus;
  /** Fetches a scope's channels once; a no-op while one is in flight or done. Retry after error. */
  loadChannels: (scope: ChannelScope) => void;
  /**
   * Top-level destinations. Each carries `available` (false when it has no states, so cannot be
   * delivered to here) and, where the vendor records it, its postcode length.
   */
  readonly countries: readonly DestinationNode[];
  /** The vendor's own "orderable" package-status codes (`package_status_for_order_list`). */
  readonly eligiblePackageStatusCodes: readonly string[];
  /** Status of the eager lists above (and every country's states), fetched together on mount. */
  readonly status: 'loading' | 'ready' | 'error';
  /** Asks the vendor again for everything above. Safe to call at any time. */
  reload: () => void;
  /**
   * A country's states. All 70 arrive with the countries in one request (MYP-88), so there is no
   * per-country load: empty means the catalog has not loaded, or the country has no states.
   */
  provincesFor: (countryCode: string) => readonly DestinationNode[];
  /** The same status as `status` for every country — the states load with the catalog. */
  provincesStatus: (countryCode: string) => CatalogFetchStatus;
  /** A province's cities, once loaded — empty until `loadCities` has resolved for it. */
  citiesFor: (provinceCode: string) => readonly DestinationNode[];
  citiesStatus: (provinceCode: string) => CatalogFetchStatus;
  loadCities: (provinceCode: string) => void;
}

export const OrderCatalogContext = createContext<OrderCatalogState | null>(null);

export function useOrderCatalog(): OrderCatalogState {
  const value = useContext(OrderCatalogContext);
  if (!value) throw new Error('useOrderCatalog must be used inside OrderCatalogProvider');
  return value;
}

/**
 * Assembles a destinations tree scoped to one draft's selection.
 *
 * `draftToAddress`/`applyDraftToOrder` (in `@mypoz/shared`) were written for MYP-53 slice 1 against
 * a single fixture tree that already held every country/province/city — they walk `.children` to
 * resolve a code to a name. This provider deliberately does not hold that whole tree (country's
 * fine at 14 rows; province and city are not), so this builds the one branch those functions
 * actually need, out of whatever this session has already fetched.
 *
 * Called at submit/save time, by which point `DeliveryForm`'s own effects have already triggered
 * `loadCities` for the current selection (states arrive with the catalog) — a customer cannot
 * reach Confirm with a state chosen and its cities still unfetched, since the city field stays
 * disabled until they load. `provincesFor`/`citiesFor` return `[]` for anything not yet cached, so a code that
 * genuinely has no data resolves to "not found" rather than throwing.
 */
export function destinationTreeFor(
  catalog: Pick<OrderCatalogState, 'countries' | 'provincesFor' | 'citiesFor'>,
  countryCode: string | undefined,
  provinceCode: string | undefined,
): readonly DestinationNode[] {
  if (!countryCode) return catalog.countries;

  const provinces = catalog.provincesFor(countryCode);
  const cities = provinceCode ? catalog.citiesFor(provinceCode) : [];

  return catalog.countries.map((country) =>
    country.code === countryCode
      ? {
          ...country,
          children: provinces.map((province) =>
            province.code === provinceCode ? { ...province, children: cities } : province,
          ),
        }
      : country,
  );
}
